package pl.nickers.match3.ecs

fun applyGravityToGrid(
    world: World,
    entitiesToRemove: Set<Int>,
    width: Int,
    height: Int,
    goalFlightEntities: Set<Int> = entitiesToRemove,
) {
    world.enqueueGoalFlightsForRemoval(goalFlightEntities)
    entitiesToRemove.forEach { world.deleteEntity(it) }

    val posMapper = world.mapper<GridPositionComponent>()
    val fallingMapper = world.mapper<FallingComponent>()
    val teleportingMapper = world.mapper<TeleportingComponent>()
    val iceCubeMapper = world.mapper<IceCubeComponent>()
    val spawnPointMapper = world.mapper<SpawnPointComponent>()
    val teleportTargetMapper = world.mapper<TeleportTargetMarkerComponent>()
    val aspect = Aspect.all(GridPositionComponent::class, JellyTypeComponent::class)

    val cellEntity = Array(height) { IntArray(width) { -1 } }
    val isMovable = Array(height) { BooleanArray(width) { false } }
    val isHole = Array(height) { BooleanArray(width) { false } }

    for (r in 0 until height) {
        for (c in 0 until width) {
            isHole[r][c] = world.isHole(r, c)
        }
    }

    for (eid in world.getEntitiesForAspect(aspect)) {
        val pos = posMapper[eid]!!
        if (pos.row in 0 until height && pos.col in 0 until width) {
            cellEntity[pos.row][pos.col] = eid
            if (!iceCubeMapper.has(eid) && !spawnPointMapper.has(eid)
                && !world.hasComponent(eid, FusedBombComponent::class)
                && !world.hasComponent(eid, ExplodingComponent::class)
            ) {
                isMovable[pos.row][pos.col] = true
            }
        }
    }

    // Map of (destRow, destCol) -> source coords for teleport markers on the board.
    val teleportTargets: Map<Pair<Int, Int>, Pair<Int, Int>> = world
        .getEntitiesForAspect(Aspect.all(GridPositionComponent::class, TeleportTargetMarkerComponent::class))
        .mapNotNull { eid ->
            val pos = posMapper[eid] ?: return@mapNotNull null
            val marker = teleportTargetMapper[eid] ?: return@mapNotNull null
            (pos.row to pos.col) to (marker.sourceRow to marker.sourceCol)
        }
        .toMap()

    fun applyMove(srcR: Int, srcC: Int, destR: Int, destC: Int, asTeleport: Boolean) {
        val eid = cellEntity[srcR][srcC]
        cellEntity[srcR][srcC] = -1
        isMovable[srcR][srcC] = false
        cellEntity[destR][destC] = eid
        isMovable[destR][destC] = true
        val pos = posMapper[eid]!!
        val fromRow = pos.row
        val fromCol = pos.col
        pos.row = destR
        pos.col = destC
        if (asTeleport) {
            teleportingMapper.set(
                eid,
                TeleportingComponent(
                    sourceRow = fromRow, sourceCol = fromCol,
                    destRow = destR, destCol = destC,
                ),
            )
        } else {
            fallingMapper.set(
                eid,
                FallingComponent(
                    fromRow = fromRow, toRow = destR,
                    fromCol = fromCol, toCol = destC,
                ),
            )
        }
    }

    fun pullPass(asTeleport: Boolean, findSource: (Int, Int) -> Pair<Int, Int>?): Boolean {
        var didMove = false
        for (r in height - 1 downTo 0) {
            for (c in 0 until width) {
                if (cellEntity[r][c] != -1 || isHole[r][c]) continue
                val source = findSource(r, c) ?: continue
                val (sr, sc) = source
                if (sr !in 0 until height || sc !in 0 until width) continue
                if (!isMovable[sr][sc]) continue
                applyMove(sr, sc, r, c, asTeleport)
                didMove = true
            }
        }
        return didMove
    }

    // Vertical pass: pull from the cell directly above.
    val verticalMoved = pullPass(asTeleport = false) { r, c ->
        if (r == 0) null else (r - 1) to c
    }

    // Teleport pass: cells with a teleport target marker pull from the linked source.
    val teleportMoved = pullPass(asTeleport = true) { r, c ->
        teleportTargets[r to c]
    }
    if (verticalMoved || teleportMoved) return

    // Diagonal pass: pull from above-left or above-right when no "straight-down"
    // source exists (normal or teleport)
    pullPass(asTeleport = false) { r, c ->
        val above = r - 1
        if (above < 0) return@pullPass null
        val leftCol = c - 1
        val rightCol = c + 1
        when {
            leftCol >= 0 && isMovable[above][leftCol] -> above to leftCol
            rightCol < width && isMovable[above][rightCol] -> above to rightCol
            else -> null
        }
    }
}
